import { RouteComponentProps } from '@reach/router'
import { Button, Modal, Select, Table, Tabs } from 'antd'
import { ColumnProps } from 'antd/lib/table'
import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { getMessages, MessageItem, MessageStatus, MessageType, modifyMessage } from '../services/message'
import { useLoading } from '../common/hooks'
import IconMsgReaded from '../assets/msg-readed.png'
import IconMsgUnread from '../assets/msg-unread.png'
import { Title } from '../components/ui'

const StyledMessage = styled.div`
  .card-content {
    box-shadow: 0px 0.6px 1.8px rgba(0, 0, 0, 0.11), 0px 3.2px 7.2px rgba(0, 0, 0, 0.133);
    width: 100%;
    height: 100%;
    padding: 40px 16px 10px;
  }

  .banner {
    width: 100%;
    height: 120px;
    margin-top: 5px;
  }

  h1 {
    margin: 70px auto 25px;
  }

  .table-x {
    background: #ffffff;
    box-shadow: 0px 0.6px 1.8px rgba(0, 0, 0, 0.11), 0px 3.2px 7.2px rgba(0, 0, 0, 0.133);
    margin-bottom: 80px;

    .ant-table-thead {
      background-color: #187c6a;

      & > tr > th {
        background-color: #187c6a;
        color: #fff;
      }
    }
  }

  .readed-icon {
    width: 18px;
    height: 18px;
    margin-right: 5px;
  }
`

export const Message: React.FC<RouteComponentProps> = () => {
  const [msgs, setMsgs] = useState([] as MessageItem[])
  const [detailIndex, setDetailIndex] = useState(-1)
  const { loading, start, finish } = useLoading()
  const [confirmLoading, setConfirmLoading] = useState(false)
  const [activeKey, setActiveKey] = useState<MessageType | string>(MessageType.ALL)
  const fetchList = async () => {
    start()
    try {
      const { dataList } = await getMessages({ status: MessageStatus.ALL, type: activeKey })
      setMsgs((dataList || []).filter((item) => item.status !== MessageStatus.Delete))
    } catch (error) {
      console.error(error)
    }
    finish()
  }
  useEffect(() => {
    ;(async () => {
      await fetchList()
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeKey])

  const delMessage = (mid: string) => {
    Modal.confirm({
      title: 'ARE YOU SURE YOU WANT TO DELETE THIS MESSAGE ?',
      content: 'This will permanently delete this message.',
      okType: 'danger',
      okText: 'Delete',
      onOk: async () => {
        await modifyMessage({ mid, status: MessageStatus.Delete })
        const index = msgs.findIndex((d) => d.mid === mid)
        setMsgs((s) => {
          return [...s.slice(0, index), ...s.slice(index + 1, s.length)]
        })
      },
    })
  }

  const readMessage = async (index: number) => {
    const mid = msgs[index].mid
    try {
      await modifyMessage({ mid, status: MessageStatus.Read })
      setDetailIndex(-1)
    } catch (error) {
      setDetailIndex(-1)
    }
    await fetchList()
  }

  const columns: ColumnProps<MessageItem>[] = [
    {
      title: 'Subject',
      key: 'subject',
      dataIndex: 'subject',
      render: (text, row) => (
        <div>
          <img className="readed-icon inline" src={row.status === MessageStatus.Read ? IconMsgReaded : IconMsgUnread} alt="read" />
          {text}
        </div>
      ),
    },
    {
      title: 'Date',
      key: 'date',
      width: 120,
      dataIndex: 'date',
    },
    {
      title: 'Sender',
      key: 'sender',
      dataIndex: 'sender',
      width: 150,
    },
    {
      title: 'Action',
      key: 'op',
      fixed: 'right',
      width: 100,
      render: (_1: string, row: MessageItem, index) => {
        return (
          <div>
            <Button type="link" onClick={() => setDetailIndex(index)}>
              Detail
            </Button>
            <Button type="link" danger onClick={() => delMessage(row.mid)}>
              Delete
            </Button>
          </div>
        )
      },
    },
  ]
  const onTabChange = (key: string) => {
    setActiveKey(key)
  }
  return (
    <StyledMessage>
      {/* <img className="banner" src={Banner} alt="banner" /> */}
      <div>
        <div className="card-content bg-gray-100">
          <div className="w-full sm:w-1/2 m-auto">
            <Title className="text-left mt-0 ml-0 mb-5">My Message</Title>
            <p className="text-lg">Never missed any messages and notifications from us.</p>
          </div>
          <div className="hidden sm:flex justify-end">
            <Tabs activeKey={activeKey} onChange={onTabChange}>
              <Tabs.TabPane key="" tab="ALL"></Tabs.TabPane>
              <Tabs.TabPane key="1" tab="Transaction Alert"></Tabs.TabPane>
              <Tabs.TabPane key="3" tab="Promotional Message"></Tabs.TabPane>
              <Tabs.TabPane key="2" tab="Notice"></Tabs.TabPane>
            </Tabs>
          </div>
          <div>
            <Select value={activeKey} className="block sm:hidden" onChange={onTabChange}>
              <Select.Option value="">ALL</Select.Option>
              <Select.Option value="1">Transaction Alert</Select.Option>
              <Select.Option value="2">Promotional Message</Select.Option>
              <Select.Option value="3">Notice</Select.Option>
            </Select>
          </div>
        </div>
      </div>

      <div className="p-0 sm:px-10 mt-5">
        <Table
          scroll={{ x: 700 }}
          className="table-x"
          loading={loading}
          rowKey="mid"
          columns={columns}
          dataSource={msgs}
          pagination={false}
          locale={{ emptyText: 'No messages found' }}
        />
      </div>
      <Modal
        visible={detailIndex >= 0}
        title={msgs[detailIndex] ? msgs[detailIndex].subject : 'Subject Detail'}
        closeIcon={null}
        maskClosable={false}
        closable={false}
        okText="Close"
        cancelText="Delete"
        cancelButtonProps={{ danger: true }}
        confirmLoading={confirmLoading}
        onCancel={async () => {
          await delMessage(msgs[detailIndex].mid)
          setDetailIndex(-1)
        }}
        onOk={async () => {
          if (msgs[detailIndex].status === MessageStatus.Unread) {
            setConfirmLoading(true)
            try {
              await readMessage(detailIndex)
              setConfirmLoading(false)
            } catch (error) {
              setConfirmLoading(false)
            }
          } else {
            setDetailIndex(-1)
          }
        }}
      >
        <pre className="whitespace-normal" dangerouslySetInnerHTML={{ __html: msgs[detailIndex] ? msgs[detailIndex].content : '' }} />
      </Modal>
    </StyledMessage>
  )
}
